import React from "react";

interface AnimatedBadgeProps {
    status: string;
    color?: string;
    className?: string;
    icon?: string;
}

const AnimatedBadge: React.FC<AnimatedBadgeProps> = (props) => {
    const { status, color, className = "", icon } = props;

    const getStatusColor = (status: string) => {
        const lowerStatus = status.toLowerCase().trim();

        // ✅ Added: Active & Inactive support (highest priority)
        if (lowerStatus === "active") return "#28a745";     // Bootstrap success green
        if (lowerStatus === "inactive") return "#dc3545";
        if (lowerStatus === "contacted") return "#30bc20ff"; // Success Green
        if (lowerStatus === "closed") return "#1ecbe2"; // Info Cyan
        if (lowerStatus === "lost") return "#EA5455"; // Danger Red
        if (lowerStatus === "not_contacted" || lowerStatus === "not contacted") return "#fda700"; // Warning Orange
        return undefined; // No default here
    };

    const isKnownStatus = (status: string) => {
        const lower = status.toLowerCase().trim();
        return ["active", "inactive", "contacted", "closed", "lost", "not_contacted", "not contacted"].includes(lower);
    };

    let badgeColor = color;

    if (!badgeColor) {
        if (isKnownStatus(status)) {
            badgeColor = getStatusColor(status);
        } else if (!className) {
            // Only default to purple if no class is provided
            badgeColor = "#7367F0";
        }
    }

    const styles = {
        badge: {
            position: "relative" as const,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0.2em 1em",
            fontSize: "0.70rem",
            fontWeight: 600,
            borderRadius: "0.4rem",
            color: badgeColor ? "#fff" : undefined, // Only force white text if using background color
            backgroundColor: badgeColor,
            overflow: "hidden",
            whiteSpace: "nowrap" as const,
            border: badgeColor ? `1px solid ${badgeColor}` : undefined,
            zIndex: 1,
        },
        shimmer: {
            position: "absolute" as const,
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
                "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
            transform: "skewX(-20deg) translateX(-150%)",
            animation: "shine 3s infinite",
        },
    };

    React.useEffect(() => {
        const styleId = "animated-badge-keyframes";
        if (!document.getElementById(styleId)) {
            const styleSheet = document.createElement("style");
            styleSheet.id = styleId;
            styleSheet.innerText = `
        @keyframes shine {
          0% {
            transform: skewX(-20deg) translateX(-150%);
          }
          50% {
             transform: skewX(-20deg) translateX(150%);
          }
          100% {
            transform: skewX(-20deg) translateX(150%);
          }
        }
      `;
            document.head.appendChild(styleSheet);
        }
    }, []);

    return (
        <span style={styles.badge} className={className}>
            {icon && <i className={`${icon} me-1`} style={{ position: "relative", zIndex: 2 }}></i>}
            <span style={{ position: "relative", zIndex: 2 }}>{status.replace(/_/g, " ")}</span>
            <span style={styles.shimmer} />
        </span>
    );
};

export default AnimatedBadge;
